import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Typography, CircularProgress } from '@material-ui/core/';
import React, { Fragment, useState, useMemo, useEffect } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  TextInput,
  SelectInput,
  TextField,
  TopToolbar,
  useRefresh,
  Pagination,
  DateField,
  SelectField,
  useListContext,
  useQueryWithStore
} from 'react-admin';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';

import dataProvider from '../../core/providers/IccUserDataProvider';
import AutocompleteInput from '../../core/components/forked/input/AutocompleteInput';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import ListTitleBar from '../../core/components/ListTitleBar';
import StatusSelectField from '../../core/components/StatusSelectField';
import { DocumentIcon } from '../../core/themes/icons/DocumentIcon';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import { documentFilterStatus, documentStatus } from './ResourceUploadLookups';
import ResourceUploadListBulkActions from './ResourceUploadListBulkActions';
import ManageDocumentPropertiesDialog from './manageBatch/ManageDocumentPropertiesDialog';
import ResourceUploadApi from '../../core/apis/ResourceUploadApi';
import { formatDate } from '../../core/utils/index';
import PopupField from '../../core/components/grid/PopupField';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import {
  DateRangeFilter,
  ConvertRangeToStartEndDate
} from '../../core/components/DateRangeFilter';
import green from '@material-ui/core/colors/green';
import orange from '@material-ui/core/colors/orange';
import red from '@material-ui/core/colors/red';
import withStyles from '@material-ui/core/styles/withStyles';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';
import { DateRangeChoices } from '../../core/components/DateRangeLookups';

const styles = {
  greenText: { color: green[500] },
  orangeText: { color: orange[500] },
  redText: { color: red[500] }
};

//create a little component for non resource table cell
const StatusByColorField = withStyles(styles)(({ classes, record }) => {
  let optionClass = '';
  switch (record?.status) {
    case 'ACTIVE':
    case 'A':
      optionClass = classes.greenText;
      break;
    case 'INVITED':
    case 'INACTIVE':
    case 'PENDING':
    case 'OFFLINE':
    case 'P':
      optionClass = classes.orangeText;
      break;
    case 'SUSPENDED':
    case 'S':
      optionClass = classes.redText;
      break;
    default:
      break;
  }

  return (
    <Typography component="span" variant="body2" className={optionClass}>
      {capitalize(record.status)}
    </Typography>
  );
});

const ShowRelatedDocuments = props => {
  const { id, classes, record } = props;
  const { loading, data } = useQueryWithStore({
    type: 'getManyReference',
    resource: 'resourceuploaddocuments',
    payload: {
      target: 'resourceUploadId',
      id: id,
      pagination: { page: 0, perPage: 10000 },
      sort: {}
    }
  });

  if (loading && !data) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
        {'Loading documents'}
      </div>
    );
  }
  const documentsFilteredByStatus = data.filter(
    document =>
      document.matchingStatus !== 'INACTIVE_INVESTOR' &&
      document.matchingStatus !== 'MISMATCH'
  );

  const tableHeadCells = [
    { id: '' },
    { id: 'Document Title' },
    { id: 'Category Name' },
    { id: 'Fund Name' },
    { id: 'Investor Name' },
    { id: 'Document Status' }
  ];

  const EnhancedTableHead = () => {
    return (
      <TableHead>
        <TableRow>
          {tableHeadCells.map(headCell => (
            <TableCell key={headCell.id}>{headCell.id}</TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };
  if (documentsFilteredByStatus.length === 0) {
    return (
      <div className={classes.noDataWrapper}>
        {record.profileData.uploadType === 'BULK'
          ? 'There are no Documents to be displayed. Please complete splitting of the batch.'
          : 'There are no Documents to be displayed. Please assign the batch to Investors.'}
      </div>
    );
  } else {
    return (
      <Paper style={{ padding: '16px 16px 10px 16px' }}>
        <span className={classes.label}>Batch Documents</span>
        <Table>
          <EnhancedTableHead
            tableHeadCells={tableHeadCells}
            rowCount={documentsFilteredByStatus?.length}
          />
          <TableBody>
            {documentsFilteredByStatus?.map((doc, index) => {
              return (
                <TableRow key={doc.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{doc.documentTitle}</TableCell>
                  <TableCell>{doc.categoryName}</TableCell>
                  <TableCell>{doc.fundName}</TableCell>
                  <TableCell>
                    {record.allInvestors ? 'All Investors' : doc.investorName}
                  </TableCell>
                  <TableCell>
                    <StatusByColorField record={doc} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    );
  }
};
const useStyles = makeStyles({
  root: {
    marginRight: 'auto'
  },
  dateFilterStyle: {
    display: 'flex',
    marginBottom: '4px'
  },
  dateInputStyle: { width: '145px', marginRight: '5px' },
  dateRangeSeparator: {
    marginTop: '25px',
    marginRight: '5px',
    color: '#757575'
  },
  autocompleteSuggestions: {
    zIndex: 3010,
    backgroundColor: '#ffffff'
  },
  progress: {
    position: 'absolute',
    top: '55%',
    left: '55%'
  },
  batchTitleFilterStyle: {
    minWidth: '200px'
  },
  compressedFilterStyle: {
    width: '140px',
    minWidth: '140px'
  },
  fundLabel: {
    lineHeight: '30px',
    height: '30px'
  },
  fundsTableRow: {
    // This classe should be removed once the services return the list of funds visible by the user
    height: 0
  },
  fundsTableCell: {
    // This classe should be removed once the services return the list of funds visible by the user
    borderBottom: 0
  },
  label: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block'
  },
  loading: {
    justifyContent: 'center',
    display: 'flex',
    padding: '16px 16px 10px 16px',
    flexDirection: 'column',
    alignItems: 'center'
  },
  noDataWrapper: {
    justifyContent: 'center',
    display: 'flex',
    padding: '16px 16px 10px 16px',
    flexDirection: 'column',
    alignItems: 'left'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();
  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const FundField = ({ record, classes, ...rest }) => {
  const fundIds = get(record, 'fundIds', []);
  if (fundIds.length > 1) {
    const getFundsList = () => {
      return fundIds.map((fundId, index) => (
        <ReferenceField
          source={'fundIds[' + index + ']'}
          reference="funds"
          link={false}
          {...rest}
        >
          <TextField source="name" className={classes.fundLabel} />
        </ReferenceField>
      ));
    };

    return (
      <div>
        <PopupField
          data={getFundsList()}
          label="Multiple Funds"
          headLabel="Funds"
          classesNames={{
            tableRow: classes.fundsTableRow,
            tableCell: classes.fundsTableCell
          }}
        />
      </div>
    );
  } else if (fundIds.length === 1) {
    if (fundIds[0] === '*') {
      return <Typography variant="body2">All Funds</Typography>;
    }
    return (
      <ReferenceField
        source="fundIds[0]"
        reference="funds"
        link={false}
        {...rest}
      >
        <TextField source="name" />
      </ReferenceField>
    );
  }
  return DocumentFundField(rest);
};

/**Display the fund id from the derived info if there is no fund id at the batch level
in the case of multiple funds display a message on the popup as the derived fund id does not contains ids*/
const DocumentFundField = props => {
  const derivedInfo = get(props.record, 'derivedInfo', {});

  if (derivedInfo.derivedFundId === 'MULTIPLE') {
    return (
      <div>
        <PopupField
          data={['The details cannot be previewed. Please open the batch']}
          label="Multiple Funds"
          headLabel="Funds"
        />
      </div>
    );
  } else {
    return (
      <ReferenceField
        source="derivedInfo.derivedFundId"
        reference="funds"
        link={false}
        {...props}
      >
        <TextField source="name" />
      </ReferenceField>
    );
  }
};

const InvestorCountField = () => {
  return (
    <ReferenceField
      source="resourceUploadId"
      reference="investorcount"
      link={false}
    >
      <FunctionField
        render={record =>
          record
            ? `${record.readInvestorsCount}/${record.totalInvestorsCount}
            (${
              record.totalInvestorsCount === 0 ||
              record.readInvestorsCount === 0
                ? 0
                : Number.isInteger(
                    (record.readInvestorsCount * 100) /
                      record.totalInvestorsCount
                  )
                ? (record.readInvestorsCount * 100) / record.totalInvestorsCount
                : (
                    (record.readInvestorsCount * 100) /
                    record.totalInvestorsCount
                  ).toFixed(2)
            }%)`
            : ''
        }
      />
    </ReferenceField>
  );
};

const DocumentFilters = props => {
  const { userProfile = {}, dateFilter, ...rest } = props;
  const classes = useStyles();
  const userId = userProfile.userId;

  // manually remove documentDateFilter elements (fromDocumentDate and toDocumentDate)
  if (
    props.displayedFilters &&
    !props.displayedFilters.documentDateFilter &&
    props.setFilters &&
    (props.filterValues.fromDocumentDate || props.filterValues.toDocumentDate)
  ) {
    props.setFilters({
      ...props.filterValues,
      fromDocumentDate: undefined,
      toDocumentDate: undefined
    });
  }

  return (
    <Filter variant="standard" {...rest}>
      <TextInput
        label="Search by Document Title"
        source="documentTitle"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
        className={classes.batchTitleFilterStyle}
      />
      <SelectInput
        label="Status"
        source="batchStatus"
        alwaysOn
        allowEmpty={false}
        choices={[{ id: '', name: 'All' }, ...documentFilterStatus]}
        SelectProps={{
          displayEmpty: true
        }}
        className={classes.compressedFilterStyle}
      />
      <DateRangeFilter
        label="Upload Date"
        source="uploadDateFilter"
        fromSource="fromUploadDate"
        toSource="toUploadDate"
        defaultFrom={props.filterValues.fromUploadDate}
        defaultTo={props.filterValues.toUploadDate}
        defaultSelectValue={props.filterValues.uploadDateFilter}
        showRange={false}
        alwaysOn
        autoSelectValues={false}
      />
      <ReferenceInput
        label="Fund Name"
        source="fundId"
        resource="funds"
        reference="funds"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
        perPage={100}
        shouldRenderSuggestions={
          value => value.trim().length >= 1 //We display the list once we have at least one char inputted
        }
        filterToQuery={searchText =>
          searchText
            ? {
                name: '*' + searchText + '*'
              }
            : null
        }
      >
        <AutocompleteInput
          optionText="name"
          autoComplete="off"
          placeholder="Enter Fund Name"
          options={{
            suggestionsContainerProps: {
              className: classes.autocompleteSuggestions
            }
          }}
          id="autocomplete-input-fund-name"
          menuId="autocomplete-input-menu-fund-name"
          getItemId={itemIndex =>
            `autocomplete-input-menu-item-fund-name-${itemIndex}`
          }
        />
      </ReferenceInput>

      <ReferenceInput
        label="Investor Name"
        source="investorId"
        resource="investors"
        reference="investors"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
        perPage={100}
        shouldRenderSuggestions={
          value => value.trim().length >= 1 //We display the list once we have at least one char inputted
        }
        filterToQuery={searchText =>
          searchText
            ? {
                name: '*' + searchText + '*'
              }
            : null
        }
      >
        <AutocompleteInput
          optionText="name"
          autoComplete="off"
          placeholder="Enter Investor Name"
          options={{
            suggestionsContainerProps: {
              className: classes.autocompleteSuggestions
            }
          }}
          id="autocomplete-input-investor-name"
          menuId="autocomplete-input-menu-investor-name"
          getItemId={itemIndex =>
            `autocomplete-input-menu-item-investor-name-${itemIndex}`
          }
        />
      </ReferenceInput>

      <ReferenceInput
        label="Category Name"
        source="categoryId"
        resource="categories"
        reference="categories"
        filter={{ subType: 'REPORTING', status: 'ACTIVE' }}
        alwaysOn
        perPage={-1}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteInput
          optionText="name"
          autoComplete="off"
          showArrowDown={true}
          clientSideFilterOnly={true}
          emptyText="All"
          options={{
            suggestionsContainerProps: {
              className: classes.autocompleteSuggestions
            }
          }}
          id="autocomplete-input-category-name"
          menuId="autocomplete-input-menu-category-name"
          getItemId={itemIndex =>
            `autocomplete-input-menu-item-category-name-${itemIndex}`
          }
        />
      </ReferenceInput>

      <TextInput
        label="Batch ID"
        source="resourceUploadId"
        allowEmpty
        alwaysOn
        options={{ autoComplete: 'off' }}
        className={classes.compressedFilterStyle}
      />

      <TextInput
        label="Search by Document Batch Title"
        source="documentBatchTitle"
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
        className={classes.batchTitleFilterStyle}
      />

      <SelectInput
        label="User"
        source="userId"
        choices={[{ id: userId, name: 'Only Documents Uploaded by me' }]}
      />

      <DateRangeFilter
        label="Document Date"
        source="documentDateFilter"
        fromSource="fromDocumentDate"
        toSource="toDocumentDate"
        defaultFrom={props.filterValues.fromDocumentDate}
        defaultTo={props.filterValues.toDocumentDate}
        defaultSelectValue={props.filterValues.documentDateFilter || '*'}
        showRange={false}
        formatDate={formatDate}
        autoSelectValues={false}
      />
    </Filter>
  );
};

const DocumentsTitle = props => {
  const { loading } = useListContext(props);
  const { setshowGridLoader, hasCreatePermission } = props;
  useEffect(() => setshowGridLoader(loading), [setshowGridLoader, loading]);

  return (
    <ListTitleBar
      type="Document Management"
      title="Upload Single Document"
      createUrl="#/resourceuploads/create?mode=single"
      secondTitle="Upload Bulk Document"
      secondCreateUrl="#/resourceuploads/create?mode=bulk"
      icon={<DocumentIcon />}
      hasCreatePermission={hasCreatePermission}
    />
  );
};

const ResourceUploadList = props => {
  const classes = useStyles();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();
  const {
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    hasReadPermission,
    permissions = [],
    userProfile,
    ...rest
  } = props;

  const [showManageDocumentsDialog, setOpen] = useState(false);
  const [selectedBatchesIds, setSelected] = useState([]);
  const [batchForEdit, setSelectedBatch] = useState({});
  const [showLoader, setShowLoader] = useState(false);
  const [showGridLoader, setshowGridLoader] = useState(false);
  const [dateFilter, setDateFilter] = useState('6_month');
  const [isDateFilterLoaded, setIsDateFilterLoaded] = useState(false);
  const [categories, setCategories] = useState([]);

  const hasApprovePermission = useMemo(() => {
    return permissions.includes('AUTH_APPROVE_RESOURCE_UPLOAD');
  }, [permissions]);

  const getBatchWithDocuments = async batchId => {
    try {
      const batch = await ResourceUploadApi.getBatchWithDocuments(batchId);
      return batch;
    } catch (e) {
      setShowLoader(false);
      notifyServerError(e);
    }
  };

  const getBatchLookups = async () => {
    try {
      let securities = dataProvider('GET_LIST', 'documentsecurities', {
        pagination: { perPage: -1 }
      });
      let subcategories = dataProvider('GET_LIST', 'subcategories', {
        pagination: { perPage: -1 },
        sort: { field: 'name', order: 'ASC' },
        filter: { status: 'ACTIVE' }
      });
      let watermarks = dataProvider('GET_LIST', 'documentwatermarks', {
        pagination: { perPage: -1 },
        sort: { field: 'profileName', order: 'ASC' }
      });
      let categories = dataProvider('GET_LIST', 'categories', {
        pagination: { perPage: -1 },
        sort: { field: 'name', order: 'ASC' },
        filter: { subType: 'REPORTING', status: 'ACTIVE' }
      });

      [securities, subcategories, watermarks, categories] = [
        await securities,
        await subcategories,
        await watermarks,
        await categories
      ];

      return {
        allSubcategories: subcategories.data,
        allWatermarks: watermarks.data,
        allSecurities: securities.data,
        allCategories: categories.data
      };
    } catch (error) {
      setShowLoader(false);
      notifyServerError(error);
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    refresh();
  };
  const handleOpen = async selectedId => {
    if (showLoader) return;
    setShowLoader(true);
    setSelected(selectedId);
    let batchForEdit = getBatchWithDocuments(selectedId);
    let batchLookups = getBatchLookups();
    [batchForEdit, batchLookups] = [await batchForEdit, await batchLookups];
    if (batchForEdit && batchForEdit.batch && batchLookups) {
      batchForEdit.lookups = batchLookups;
      setSelectedBatch(batchForEdit);
      setShowLoader(false);
      setOpen(true);
    }
  };

  useEffect(() => {
    const fetchDocMngtDefaultDateValue = async () => {
      setIsDateFilterLoaded(false);
      TenantConfigurationApi.get(
        'Document_management_default_upload_date_filter'
      )
        .then(response => {
          if (response.pageData[0].value) {
            const entry = DateRangeChoices.filter(
              item =>
                item.name.indexOf(response.pageData[0].value.trim()) !== -1
            );
            if (entry.length === 1) {
              setDateFilter(entry[0].id);
            }
          }
          setIsDateFilterLoaded(true);
        })
        .catch(error => notifyServerError(error, null));
    };
    const getCategories = async () => {
      const categoriesGet = dataProvider('GET_LIST', 'categories', {
        pagination: { perPage: -1 },
        sort: { field: 'name', order: 'ASC' },
        filter: { subType: 'REPORTING', status: 'ACTIVE' }
      });
      const request = await categoriesGet;
      var data = request.data;
      data.forEach(v => (v.id = v.id.toString()));
      data.push({
        id: 'MULTIPLE',
        name: 'Multiple Categories'
      });
      setCategories(data);
    };
    fetchDocMngtDefaultDateValue();
    getCategories();
  }, [notifyServerError, setDateFilter, setIsDateFilterLoaded, setCategories]);

  const uploadDateFilterDefaultRange = ConvertRangeToStartEndDate(dateFilter);

  return (
    <Fragment>
      {isDateFilterLoaded && (
        <List
          {...rest}
          debounce={1000}
          sort={{ field: 'id', order: 'DESC' }}
          filters={
            <DocumentFilters
              userProfile={userProfile}
              dateFilter={dateFilter}
            />
          }
          filterDefaultValues={{
            batchStatus: 'PENDING',
            uploadDateFilter: dateFilter,
            fromUploadDate: uploadDateFilterDefaultRange.startString,
            toUploadDate: uploadDateFilterDefaultRange.endString
          }}
          title={
            <DocumentsTitle
              hasCreatePermission={hasCreatePermission}
              setshowGridLoader={setshowGridLoader}
            />
          }
          exporter={false}
          perPage={20}
          pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
          bulkActionButtons={
            (hasDeletePermission ||
              hasEditPermission ||
              hasApprovePermission) && (
              <ResourceUploadListBulkActions
                hasDeletePermission={hasDeletePermission}
                hasEditPermission={hasEditPermission}
                hasApprovePermission={hasApprovePermission}
                handleManageDocumentPropertiesOpen={handleOpen}
                showLoader={showLoader}
                setShowLoader={setShowLoader}
              />
            )
          }
          actions={<Actions />}
          empty={<Empty />}
          syncWithLocation
        >
          <Datagrid expand={<ShowRelatedDocuments classes={classes} />}>
            {hasCreatePermission ? (
              <LinkField
                source="documentTitle"
                label="Document Batch Title"
                idSource="resourceUploadId"
                id="document-title"
              />
            ) : (
              <TextField
                label="Document Batch Title"
                source="documentTitle"
                id="document-title"
              />
            )}
            <TextField
              label="Document Batch File Name"
              source="fileName"
              sortable={false}
              id="filename"
            />
            <SelectField
              label="Category Name"
              source="derivedInfo.derivedCategoryId"
              link={false}
              sortable={false}
              choices={categories}
              id="category"
            />
            <TextField
              label="Batch ID"
              source="resourceUploadId"
              sortBy="id"
              id="batch-id"
            />
            <FundField label="Fund Name" classes={classes} id="fund-name" />
            <InvestorCountField
              label="Read by No. of Investors"
              id="read-by-investors"
            />
            <DateField
              label="Upload Date"
              source="uploadDate"
              sortable={false}
              id="upload-date"
            />
            <FunctionField
              label="Uploaded By"
              render={record =>
                `${
                  record.uploadedByFirstName ? record.uploadedByFirstName : ''
                } ${record.uploadedByLastName ? record.uploadedByLastName : ''}`
              }
              id="uploaded-by"
            />
            <StatusSelectField
              label="Status"
              source="batchStatus"
              choices={documentStatus}
              sortable={false}
              id="status"
              style={{ wordBreak: 'keep-all' }}
            />
          </Datagrid>
        </List>
      )}
      {showManageDocumentsDialog && (
        <ManageDocumentPropertiesDialog
          showDialog={showManageDocumentsDialog}
          onClose={handleDialogClose}
          id={selectedBatchesIds}
          batchForEdit={batchForEdit}
          title=" "
          {...props}
        />
      )}
      {(showLoader || showGridLoader) && (
        <div className={classes.progress}>
          <CircularProgress />
        </div>
      )}
    </Fragment>
  );
};

export default ResourceUploadList;
